import { yupResolver } from "@hookform/resolvers/yup";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import AutocompleteSelect from "../../../../ui/components/autocomplete-select/autocomplete-select";
import CustomInput from "../../../../ui/components/custom-input/custom-input";
import CustomSelect from "../../../../ui/components/custom-select/custom-select";
import DateInput from "../../../../ui/components/date-input/date-input";
import { ConvenioCreateForm } from "../../../models/convenio-create";
import { ConvenioPractica } from "../../../models/convenio-practica";
import { ListModel } from "../../../models/list-model";
import { ReemplazoConfirmado } from "../../../models/practica-conflicto";
import { UploadConvenio } from "../../../models/upload-convenio";
import { convenioServices } from "../../../services/convenio.services";
import { obrasocialServices } from "../../../services/obra-social.services";
import { getFormattedDate, getFormattedDecimal } from "../../../utils/common.utils";
import { TIPO_ANTIGUEDAD_OPTIONS } from "../../../utils/convenio.utils";
import "./convenio-create.scss";

const STEPS = ["Datos del convenio", "Archivo de prácticas", "Revisión"];

const schema = yup.object().shape({
  obraSocialId: yup.string().required("Campo requerido"),
  nombre: yup.string().required("Campo requerido"),
  fechaInicio: yup.date().required("Campo requerido").typeError("Fecha inválida"),
  fechaFin: yup.date().nullable(),
  consideraAntiguedad: yup.boolean().required(),
  tipoAntiguedad: yup.string().when("consideraAntiguedad", {
    is: true,
    then: (s) => s.required("Campo requerido"),
  }),
  fechaRecategorizacion: yup.date().nullable(),
});

export default function ConvenioCreate() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [obrasSociales, setObrasSociales] = useState<ListModel[]>([]);

  const [archivo, setArchivo] = useState<File | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [errorArchivo, setErrorArchivo] = useState("");

  const [resultado, setResultado] = useState<UploadConvenio | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [recargandoArchivo, setRecargandoArchivo] = useState(false);
  const [errorRecarga, setErrorRecarga] = useState("");
  const [errorConfirmar, setErrorConfirmar] = useState("");
  const [modalErroresAbierto, setModalErroresAbierto] = useState(false);
  const [seleccionReemplazos, setSeleccionReemplazos] = useState<
    Record<string, boolean>
  >({});

  const {
    control,
    watch,
    register,
    getFieldState,
    formState,
    handleSubmit,
    formState: { errors },
  } = useForm<ConvenioCreateForm>({
    mode: "all",
    resolver: yupResolver(schema) as any,
    defaultValues: {
      obraSocialId: "",
      nombre: "",
      consideraAntiguedad: false,
    },
  });

  const consideraAntiguedad = watch("consideraAntiguedad");

  useEffect(() => {
    obrasocialServices
      .lookup()
      .then((response) => {
        const items: ListModel[] = response.data.map((element: any) => ({
          value: element.id,
          label: element.value,
        }));
        setObrasSociales(items);
      })
      .catch(() => {});
  }, []);

  const onSubmitDatos = handleSubmit(() => {
    setActiveStep(1);
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    setArchivo(files[0]);
    setErrorArchivo("");
    event.target.value = "";
  };

  const subirArchivo = async (data: ConvenioCreateForm) => {
    if (!archivo) {
      setErrorArchivo("Debe seleccionar un archivo.");
      return;
    }

    setSubiendo(true);
    setErrorArchivo("");

    try {
      const formData = new FormData();
      formData.append("ObraSocialId", data.obraSocialId);
      formData.append("Nombre", data.nombre);
      formData.append("FechaInicio", data.fechaInicio.toISOString());
      if (data.fechaFin) {
        formData.append("FechaFin", data.fechaFin.toISOString());
      }
      formData.append("ConsideraAntiguedad", String(data.consideraAntiguedad));
      if (data.consideraAntiguedad && data.tipoAntiguedad) {
        formData.append("TipoAntiguedad", data.tipoAntiguedad);
      }
      if (data.fechaRecategorizacion) {
        formData.append(
          "FechaRecategorizacion",
          data.fechaRecategorizacion.toISOString()
        );
      }
      formData.append("File", archivo);

      const response = await convenioServices.upload(formData);
      setResultado(response.data);
      // Por defecto se pre-tildan los conflictos que tienen una práctica
      // equivalente sugerida en el nuevo convenio (mismo rango de antigüedad).
      const seleccionInicial: Record<string, boolean> = {};
      (response.data.practicasEnConflicto || []).forEach((conflicto) => {
        if (conflicto.reemplazo) seleccionInicial[conflicto.practicaId] = true;
      });
      setSeleccionReemplazos(seleccionInicial);
      setActiveStep(2);
    } catch (error: any) {
      let mensaje = "Error al importar el archivo.";
      if (error.response?.data) {
        if (typeof error.response.data === "string") {
          mensaje = error.response.data;
        } else if (error.response.data.message) {
          mensaje = error.response.data.message;
        }
      }
      setErrorArchivo(mensaje);
    } finally {
      setSubiendo(false);
    }
  };

  const toggleSeleccionReemplazo = (practicaId: string) => {
    setSeleccionReemplazos((prev) => ({
      ...prev,
      [practicaId]: !prev[practicaId],
    }));
  };

  const toggleSeleccionarTodos = (checked: boolean) => {
    if (!resultado) return;
    const seleccion: Record<string, boolean> = {};
    resultado.practicasEnConflicto.forEach((conflicto) => {
      if (conflicto.reemplazo) seleccion[conflicto.practicaId] = checked;
    });
    setSeleccionReemplazos(seleccion);
  };

  const exportarConflictos = () => {
    if (!resultado || resultado.practicasEnConflicto.length === 0) return;

    const escapeCsv = (value: string) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const headers = [
      "Fecha",
      "Paciente",
      "Estado",
      "Convenio actual",
      "Nº Práctica actual",
      "Nombre práctica actual",
      "Precio actual",
      "Nº Práctica nueva (sugerida)",
      "Nombre práctica nueva (sugerida)",
      "Precio nueva (sugerida)",
      "Seleccionada para reasignar",
    ];
    const filas = resultado.practicasEnConflicto.map((conflicto) => {
      const seleccionada =
        conflicto.reemplazo && seleccionReemplazos[conflicto.practicaId] ? "Sí" : "No";
      return [
        getFormattedDate(conflicto.fecha.toString()),
        conflicto.paciente,
        conflicto.estado,
        conflicto.convenioActualNombre,
        conflicto.numeroPracticaActual,
        conflicto.descripcionActual,
        getFormattedDecimal(conflicto.precioActual),
        conflicto.reemplazo?.numeroPractica ?? "",
        conflicto.reemplazo?.descripcion ?? "",
        conflicto.reemplazo ? getFormattedDecimal(conflicto.reemplazo.precio) : "",
        seleccionada,
      ]
        .map(escapeCsv)
        .join(";");
    });

    const csv = [headers.map(escapeCsv).join(";"), ...filas].join("\r\n");
    // BOM para que Excel detecte UTF-8 y respete tildes/ñ.
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const nombreArchivo = resultado.convenio.nombre.replace(/[^a-z0-9]+/gi, "_");
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `practicas-a-reasignar_${nombreArchivo}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const confirmar = () => {
    if (!resultado) return;
    setConfirmando(true);
    setErrorConfirmar("");
    const reemplazos: ReemplazoConfirmado[] = resultado.practicasEnConflicto
      .filter(
        (conflicto) => conflicto.reemplazo && seleccionReemplazos[conflicto.practicaId]
      )
      .map((conflicto) => ({
        practicaId: conflicto.practicaId,
        convenioPracticaPrecioId: conflicto.reemplazo!.convenioPracticaPrecioId,
      }));
    convenioServices
      .confirmar(resultado.convenio.id, reemplazos)
      .then((response) => {
        const reasignadas = response.data.practicasReasignadas;
        navigate("/convenios", {
          state: {
            textAlert:
              `El convenio ${resultado.convenio.nombre} fue creado correctamente.` +
              (reasignadas
                ? ` Se reasignaron ${reasignadas} práctica(s) al nuevo convenio.`
                : ""),
          },
        });
      })
      .catch((error) => {
        const mensaje =
          error.response?.data?.errors?.nonFieldError
            ?.map((e: { message: string }) => e.message)
            .join(" ") || "No se pudo confirmar el convenio. Intente nuevamente.";
        setErrorConfirmar(mensaje);
      })
      .finally(() => setConfirmando(false));
  };

  const volverACargarArchivo = () => {
    if (!resultado) return;
    setRecargandoArchivo(true);
    setErrorRecarga("");
    convenioServices
      .anular(resultado.convenio.id)
      .then(() => {
        setResultado(null);
        setArchivo(null);
        setModalErroresAbierto(false);
        setErrorArchivo("");
        setErrorConfirmar("");
        setSeleccionReemplazos({});
        setActiveStep(1);
      })
      .catch(() => {
        setErrorRecarga(
          "No se pudo descartar el archivo anterior. Intente nuevamente."
        );
      })
      .finally(() => setRecargandoArchivo(false));
  };

  const cancelar = () => {
    if (!resultado) {
      navigate("/convenios");
      return;
    }
    setCancelando(true);
    convenioServices
      .anular(resultado.convenio.id)
      .then(() => navigate("/convenios"))
      .catch(() => navigate("/convenios"))
      .finally(() => setCancelando(false));
  };

  const inputProps = { register, getFieldState, formState, errors };
  // AutocompleteSelect renders an uncontrolled branch when `register` is passed,
  // which conflicts with the controlled value/onChange wired via Controller here.
  const selectProps = { getFieldState, formState, errors };

  return (
    <div className="convenio-create-container">
      <div className="convenio-create-header">
        <button
          type="button"
          className="convenio-back-btn"
          onClick={() => navigate("/convenios")}
          title="Volver"
        >
          <ArrowBackIosNewOutlinedIcon fontSize="medium" />
        </button>
        <Typography variant="h5" gutterBottom>
          Nuevo Convenio
        </Typography>
      </div>

      <Stepper activeStep={activeStep} className="convenio-create-stepper">
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && (
        <Box component="form" autoComplete="off" noValidate onSubmit={onSubmitDatos}>
          <div className="row">
            <Controller
              name="obraSocialId"
              control={control}
              render={({ field }) => (
                <AutocompleteSelect
                  name="obraSocialId"
                  label="Obra Social"
                  isMultiple={false}
                  options={obrasSociales}
                  value={field.value}
                  onChange={(value) => field.onChange(value)}
                  required
                  {...selectProps}
                ></AutocompleteSelect>
              )}
            />
            <CustomInput
              name="nombre"
              label="Nombre del convenio"
              type="text"
              required
              maxLength={200}
              {...inputProps}
            ></CustomInput>
          </div>
          <div className="row">
            <Controller
              name="fechaInicio"
              control={control}
              render={({ field }) => (
                <DateInput
                  field={field}
                  name="fechaInicio"
                  label="Fecha inicio"
                  required
                  value={field.value}
                  {...inputProps}
                ></DateInput>
              )}
            />
            <Controller
              name="fechaFin"
              control={control}
              render={({ field }) => (
                <DateInput
                  field={field}
                  name="fechaFin"
                  label="Fecha fin (opcional)"
                  value={field.value}
                  {...inputProps}
                ></DateInput>
              )}
            />
          </div>
          <div className="row">
            <Controller
              name="consideraAntiguedad"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Considera antigüedad"
                />
              )}
            />
          </div>
          {consideraAntiguedad && (
            <div className="row">
              <Controller
                name="tipoAntiguedad"
                control={control}
                render={({ field }) => (
                  <CustomSelect
                    field={field}
                    name="tipoAntiguedad"
                    label="Tipo de antigüedad"
                    isMultiple={false}
                    required
                    options={TIPO_ANTIGUEDAD_OPTIONS}
                    {...inputProps}
                  ></CustomSelect>
                )}
              />
              <Controller
                name="fechaRecategorizacion"
                control={control}
                render={({ field }) => (
                  <DateInput
                    field={field}
                    name="fechaRecategorizacion"
                    label="Fecha de recategorización (opcional)"
                    value={field.value}
                    {...inputProps}
                  ></DateInput>
                )}
              />
            </div>
          )}
          <div className="row actions">
            <Button variant="contained" type="submit">
              Siguiente
            </Button>
          </div>
        </Box>
      )}

      {activeStep === 1 && (
        <div className="archivo-step">
          <Typography variant="body1" sx={{ mb: 2 }}>
            Seleccione el archivo Excel (.xlsx) con las prácticas y precios
            del convenio.
          </Typography>

          {errorArchivo && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorArchivo("")}>
              {errorArchivo}
            </Alert>
          )}

          <input
            id="convenio-file-input"
            type="file"
            accept=".xlsx"
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />
          <div className="archivo-selector">
            <Button
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={() =>
                document.getElementById("convenio-file-input")?.click()
              }
              disabled={subiendo}
            >
              Seleccionar Archivo
            </Button>
            {archivo && (
              <div className="archivo-seleccionado">
                <InsertDriveFileIcon fontSize="small" />
                <span>{archivo.name}</span>
                <span className="archivo-size">
                  ({(archivo.size / 1024).toFixed(2)} KB)
                </span>
              </div>
            )}
          </div>

          <div className="row actions">
            <Button
              variant="outlined"
              onClick={() => setActiveStep(0)}
              disabled={subiendo}
            >
              Atrás
            </Button>
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={handleSubmit(subirArchivo)}
              disabled={subiendo || !archivo}
            >
              {subiendo ? "Subiendo..." : "Cargar Archivo"}
            </Button>
          </div>
        </div>
      )}

      {activeStep === 2 && resultado && (
        <div className="revision-step">
          <div className="convenio-resumen">
            <Typography variant="subtitle1">
              <strong>{resultado.convenio.nombre}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {resultado.practicas.length} prácticas cargadas
            </Typography>
          </div>

          {errorRecarga && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setErrorRecarga("")}
            >
              {errorRecarga}
            </Alert>
          )}

          {resultado.tieneErrores && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={
                <Button
                  size="small"
                  startIcon={<ErrorOutlineIcon />}
                  onClick={() => setModalErroresAbierto(true)}
                >
                  Ver Detalles
                </Button>
              }
            >
              Se encontraron {resultado.lineasConError.length} líneas con
              advertencias.
            </Alert>
          )}

          {resultado.practicasEnConflicto.length > 0 && (
            <div className="conflicto-section">
              <Alert severity="warning" icon={<WarningAmberOutlinedIcon />} sx={{ mb: 1.5 }}>
                Se encontraron {resultado.practicasEnConflicto.length} práctica(s)
                ya cargadas con estado <strong>Pendiente</strong> cuya fecha cae
                dentro del rango de este convenio y coinciden (por número o
                nombre) con prácticas del archivo importado. Elija cuáles quiere
                reasignar al nuevo convenio.
              </Alert>

              <div className="conflicto-header-row">
                <Typography variant="body2" color="text.secondary">
                  {
                    Object.values(seleccionReemplazos).filter(Boolean).length
                  }{" "}
                  de{" "}
                  {
                    resultado.practicasEnConflicto.filter((c) => c.reemplazo)
                      .length
                  }{" "}
                  seleccionadas para reasignar
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FileDownloadOutlinedIcon />}
                  onClick={exportarConflictos}
                >
                  Exportar listado
                </Button>
              </div>

              <TableContainer component={Paper} className="conflicto-table">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" rowSpan={2}>
                        <Checkbox
                          size="small"
                          checked={
                            resultado.practicasEnConflicto.some((c) => c.reemplazo) &&
                            resultado.practicasEnConflicto
                              .filter((c) => c.reemplazo)
                              .every((c) => seleccionReemplazos[c.practicaId])
                          }
                          indeterminate={
                            resultado.practicasEnConflicto.some(
                              (c) => c.reemplazo && seleccionReemplazos[c.practicaId]
                            ) &&
                            !resultado.practicasEnConflicto
                              .filter((c) => c.reemplazo)
                              .every((c) => seleccionReemplazos[c.practicaId])
                          }
                          onChange={(e) => toggleSeleccionarTodos(e.target.checked)}
                          disabled={
                            !resultado.practicasEnConflicto.some((c) => c.reemplazo)
                          }
                        />
                      </TableCell>
                      <TableCell rowSpan={2}>Fecha</TableCell>
                      <TableCell rowSpan={2}>Paciente</TableCell>
                      <TableCell rowSpan={2}>Convenio actual</TableCell>
                      <TableCell colSpan={3} align="center" className="conflicto-group-header">
                        Práctica actual
                      </TableCell>
                      <TableCell rowSpan={2} />
                      <TableCell colSpan={3} align="center" className="conflicto-group-header">
                        Práctica nueva (sugerida)
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Nº</TableCell>
                      <TableCell>Nombre</TableCell>
                      <TableCell align="right">Precio</TableCell>
                      <TableCell>Nº</TableCell>
                      <TableCell>Nombre</TableCell>
                      <TableCell align="right">Precio</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {resultado.practicasEnConflicto.map((conflicto) => (
                      <TableRow
                        key={conflicto.practicaId}
                        className={!conflicto.reemplazo ? "sin-reemplazo" : ""}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={!!seleccionReemplazos[conflicto.practicaId]}
                            onChange={() =>
                              toggleSeleccionReemplazo(conflicto.practicaId)
                            }
                            disabled={!conflicto.reemplazo}
                          />
                        </TableCell>
                        <TableCell>
                          {getFormattedDate(conflicto.fecha.toString())}
                        </TableCell>
                        <TableCell>{conflicto.paciente}</TableCell>
                        <TableCell>{conflicto.convenioActualNombre}</TableCell>
                        <TableCell>{conflicto.numeroPracticaActual}</TableCell>
                        <TableCell>{conflicto.descripcionActual}</TableCell>
                        <TableCell align="right">
                          {getFormattedDecimal(conflicto.precioActual)}
                        </TableCell>
                        <TableCell align="center" className="conflicto-arrow-cell">
                          <ArrowForwardIcon
                            fontSize="small"
                            color={conflicto.reemplazo ? "action" : "disabled"}
                          />
                        </TableCell>
                        {conflicto.reemplazo ? (
                          <>
                            <TableCell>{conflicto.reemplazo.numeroPractica}</TableCell>
                            <TableCell>{conflicto.reemplazo.descripcion}</TableCell>
                            <TableCell align="right">
                              {getFormattedDecimal(conflicto.reemplazo.precio)}
                            </TableCell>
                          </>
                        ) : (
                          <TableCell colSpan={3} className="sin-reemplazo-label">
                            Sin práctica equivalente en el nuevo convenio
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          )}

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nº Práctica</TableCell>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Tipo</TableCell>
                  {consideraAntiguedad && (
                    <>
                      <TableCell>Antigüedad Mínima</TableCell>
                      <TableCell>Antigüedad Máxima</TableCell>
                    </>
                  )}
                  <TableCell align="right">Precio</TableCell>
                  <TableCell align="right">Coseguro</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {resultado.practicas.map((practica: ConvenioPractica) => (
                  <TableRow key={practica.id}>
                    <TableCell>{practica.numeroPractica}</TableCell>
                    <TableCell>{practica.descripcion}</TableCell>
                    <TableCell>{practica.tipo}</TableCell>
                    {consideraAntiguedad && (
                      <>
                        <TableCell>{practica.antiguedadMinima}</TableCell>
                        <TableCell>{practica.antiguedadMaxima}</TableCell>
                      </>
                    )}
                    <TableCell align="right">
                      {getFormattedDecimal(practica.precio)}
                    </TableCell>
                    <TableCell align="right">
                      {getFormattedDecimal(practica.coseguro)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {errorConfirmar && (
            <Alert
              severity="error"
              sx={{ mt: 2 }}
              onClose={() => setErrorConfirmar("")}
            >
              {errorConfirmar}
            </Alert>
          )}

          <div className="row actions">
            <Button
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={volverACargarArchivo}
              disabled={confirmando || cancelando || recargandoArchivo}
            >
              {recargandoArchivo ? "Descartando..." : "Volver a cargar archivo"}
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={cancelar}
              disabled={confirmando || cancelando || recargandoArchivo}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={confirmar}
              disabled={confirmando || cancelando || recargandoArchivo}
            >
              {confirmando ? "Confirmando..." : "Confirmar"}
            </Button>
          </div>
        </div>
      )}

      <Dialog
        open={modalErroresAbierto}
        onClose={() => setModalErroresAbierto(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Advertencias del archivo importado</DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Línea</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Detalle</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {resultado?.lineasConError.map((error, index) => (
                  <TableRow key={index}>
                    <TableCell>{error.lineaNumero}</TableCell>
                    <TableCell>{error.descripcion}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalErroresAbierto(false)} variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
